import { Switch } from "@/components/ui/switch";
import { isHiddenSectionEnabled } from "@shared/hidden-sections";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info } from "lucide-react";
import {
  DEVICE_MODULES,
  OPERATOR_ADDONS,
  OPERATOR_MODULES,
  PM_ADDONS,
  VENDOR_PRIVACY_FEE_CENTS,
  moduleComingSoonLabel,
  addonComingSoonLabel,
  type ModuleKey,
} from "@shared/modular-pricing";
import {
  MAX_OWNERS_INPUT,
  MAX_VENDORS_INPUT,
  type PlanSelection,
  type PlanSelectionAction,
} from "./plan-selection";

function ModuleRow({
  id,
  label,
  price,
  checked,
  included,
  onChange,
  note,
  description,
  comingSoon,
}: {
  id: string;
  label: string;
  price: string;
  checked: boolean;
  included: boolean;
  onChange: (value: boolean) => void;
  note?: string;
  description?: string;
  /** "Coming <date>" label — renders the row visible but unselectable. */
  comingSoon?: string | null;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2" data-testid={`row-module-${id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${comingSoon ? "text-gray-500" : "text-gray-900"}`}>{label}</span>
          {description && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 shrink-0"
                  aria-label={`About ${label}`}
                  data-testid={`popover-trigger-${id}`}
                >
                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align="center" className="max-w-xs text-xs leading-relaxed">
                {description}
              </PopoverContent>
            </Popover>
          )}
          {comingSoon ? (
            <Badge
              variant="secondary"
              className="bg-amber-50 text-amber-700 border-amber-200"
              data-testid={`badge-coming-soon-${id}`}
            >
              {comingSoon}
            </Badge>
          ) : (
            included && (
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`badge-included-${id}`}>
                Included in Pro
              </Badge>
            )
          )}
        </div>
        {note && <p className="text-xs text-gray-500 mt-0.5">{note}</p>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className={`text-sm ${comingSoon ? "text-gray-300" : "text-gray-500"}`}>{price}</span>
        <Switch
          checked={!comingSoon && (included || checked)}
          disabled={!!comingSoon || included}
          onCheckedChange={onChange}
          aria-label={label}
          data-testid={`switch-module-${id}`}
        />
      </div>
    </div>
  );
}

function CountRow({
  id,
  label,
  note,
  value,
  max,
  onChange,
  description,
}: {
  id: string;
  label: string;
  note: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
  description?: string;
}) {
  return (
    <div className="py-2 space-y-2" data-testid={`row-count-${id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-2">
            <Label htmlFor={id} className="text-sm font-medium text-gray-900">
              {label}
            </Label>
            {description && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 shrink-0"
                    aria-label={`About ${label}`}
                    data-testid={`popover-trigger-${id}`}
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" align="center" className="max-w-xs text-xs leading-relaxed">
                  {description}
                </PopoverContent>
              </Popover>
            )}
          </span>
          <p className="text-xs text-gray-500 mt-0.5">{note}</p>
        </div>
        <Input
          id={id}
          type="number"
          min={0}
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          className="w-24 text-right shrink-0"
          data-testid={`input-${id}`}
        />
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 pt-4 pb-1">{children}</h4>;
}

/**
 * Step 4: modules and add-ons. Operator modules + Devices are à la carte on
 * Essentials and render as locked-in "Included" on Pro. Revenue/Research are
 * operator-only; Owner Portals + CPM are PM-only. The vendors-off-marketplace
 * count applies to both kinds.
 */
export function ModuleToggles({
  selection,
  dispatch,
  realOwnerData,
}: {
  selection: PlanSelection;
  dispatch: (action: PlanSelectionAction) => void;
  realOwnerData?: { portalOwners: number; waivedOwners: number };
}) {
  const pro = selection.planTier === "pro";
  const operator = selection.accountKind === "operator";

  return (
    <div className="divide-y divide-gray-100">
      <div>
        <SectionTitle>Operator add-ons</SectionTitle>
        {(Object.keys(OPERATOR_MODULES) as Array<keyof typeof OPERATOR_MODULES>).map((key) => (
          <ModuleRow
            key={key}
            id={key}
            label={OPERATOR_MODULES[key].label}
            price={`$${OPERATOR_MODULES[key].perRoomCents / 100}/room`}
            checked={selection.modules.includes(key)}
            included={pro}
            onChange={() => dispatch({ type: "toggleModule", module: key as ModuleKey })}
            description={OPERATOR_MODULES[key].description}
            comingSoon={moduleComingSoonLabel(key)}
          />
        ))}
      </div>

      <div>
        <SectionTitle>Devices</SectionTitle>
        {(Object.keys(DEVICE_MODULES) as Array<keyof typeof DEVICE_MODULES>).map((key) => (
          <ModuleRow
            key={key}
            id={key}
            label={DEVICE_MODULES[key].label}
            price={`$${DEVICE_MODULES[key].perRoomCents / 100}/room`}
            checked={selection.modules.includes(key)}
            included={pro}
            onChange={() => dispatch({ type: "toggleModule", module: key as ModuleKey })}
            description={DEVICE_MODULES[key].description}
            note={
              key === "locks"
                ? "TTLock, Sifely & PadPulse locks integrate free; other third-party locks carry an additional $2.50/lock pass-through"
                : undefined
            }
          />
        ))}
      </div>

      {operator && (
        <div data-testid="section-operator-only">
          <SectionTitle>Financial & research (operator only)</SectionTitle>
          <ModuleRow
            id="revenue"
            label={OPERATOR_ADDONS.revenue.label}
            price={`$${OPERATOR_ADDONS.revenue.flatCents / 100} + $${OPERATOR_ADDONS.revenue.perRoomCents / 100}/room`}
            checked={selection.revenueAddon}
            included={false}
            comingSoon={addonComingSoonLabel("revenue")}
            onChange={(v) => dispatch({ type: "setRevenueAddon", value: v })}
            description={OPERATOR_ADDONS.revenue.description}
            note="Mercury-connected income & expense suite. Base PadSplit income is free on every plan."
          />
          <ModuleRow
            id="research"
            label={OPERATOR_ADDONS.research.label}
            price={`$${OPERATOR_ADDONS.research.flatCents / 100}/mo`}
            checked={selection.researchAddon}
            included={false}
            onChange={(v) => dispatch({ type: "setResearchAddon", value: v })}
            description={OPERATOR_ADDONS.research.description}
            note="Unlocks the full acquisition toolkit: Market Research, Buy Box, Underwriting, Buy, Sell & Renovate."
          />
        </div>
      )}

      {!operator && (
        <div data-testid="section-pm-only">
          <SectionTitle>Property manager (PM only)</SectionTitle>
          <CountRow
            id="owners-count"
            label={`${PM_ADDONS.owner_portals.label} — $${PM_ADDONS.owner_portals.perOwnerCents / 100}/owner/mo`}
            note="Portal + invoicing per owner. Owners subscribed to Research, Revenue, or Analytics have their portal fee waived."
            value={realOwnerData ? realOwnerData.portalOwners : selection.ownersCount}
            max={MAX_OWNERS_INPUT}
            onChange={(v) => dispatch({ type: "setOwners", value: v })}
          />
          {realOwnerData ? (
            <p className="text-xs text-emerald-700 pb-2" data-testid="text-owner-waiver-real">
              {realOwnerData.waivedOwners} of {realOwnerData.portalOwners} owners have their Owner Portal fee
              waived (subscribed to Research, Revenue, or Analytics).
            </p>
          ) : (
            selection.ownersCount > 0 && (
              <div className="pb-2 space-y-1" data-testid="control-owner-waiver-percent">
                <Label className="text-xs text-gray-500">
                  % of owners with a waiving package ({selection.waivingOwnerPercent}%)
                </Label>
                <Slider
                  value={[selection.waivingOwnerPercent]}
                  min={0}
                  max={100}
                  step={5}
                  onValueChange={([v]) => dispatch({ type: "setWaivingPercent", value: v })}
                  aria-label="Percent of owners with a waiving package"
                />
              </div>
            )
          )}
          {/* B19 / D24 — the Certified PM listing add-on is hidden (shared/hidden-sections.ts). */}
          {isHiddenSectionEnabled("certified_pm_listing") && (
            <ModuleRow
              id="cpm"
              label={PM_ADDONS.cpm_listing.label}
              price={`$${PM_ADDONS.cpm_listing.flatCents / 100}/mo`}
              checked={selection.cpmListing}
              included={false}
              onChange={(v) => dispatch({ type: "setCpmListing", value: v })}
              note="Get listed in the Certified PM network."
            />
          )}
        </div>
      )}

      {/* B19 / D24 — the $19 private-vendor fee is cut (shared/hidden-sections.ts). */}
      {isHiddenSectionEnabled("private_vendor_fee") && (
      <div>
        <SectionTitle>Vendor marketplace</SectionTitle>
        <CountRow
          id="vendors-off-marketplace"
          label={`Vendors kept off Marketplace — $${VENDOR_PRIVACY_FEE_CENTS / 100}/vendor/mo`}
          note="Vendors default to the shared Vendor Marketplace. Keep any vendor private to your portfolio for $19/mo each."
          description="Vendors you add will have the option to upgrade to a marketplace profile to be found and earn more for $19/month. If you would like to keep one or more of your vendors private, this option is for you."
          value={selection.offMarketplaceVendors}
          max={MAX_VENDORS_INPUT}
          onChange={(v) => dispatch({ type: "setVendors", value: v })}
        />
      </div>
      )}
    </div>
  );
}
